// Runtime token access helper (optional)
import tokens from './tokens.json';
// Ensure global CSS layers are included when theme/index.js is imported
import './tokens.css';
import './tokens-dark.css';
import './semantic.css';
import './breakpoints.css';
import './responsive.css';
import './buttons.css';
import './checkbox.css';

// Import theme registry
import { THEMES, THEME_IDS } from './themes.js';

export const designTokens = tokens;

// Re-export theme constants
export { THEMES, THEME_IDS };

// Convenience mapping (semantic) for JS usage
export const semantic = {
  colors: {
    primary: 'var(--color-brand-primary)',
    text: 'var(--color-text-primary)',
    surface: 'var(--color-bg-surface)',
    border: 'var(--color-border-default)'
  },
  radius: {
    card: 'var(--radius-lg)'
  },
  shadow: {
    card: 'var(--shadow-sm)'
  }
};

// ============================================================================
// Theme Storage & Validation
// ============================================================================

// Single persistent storage key for theme selection
export const THEME_STORAGE_KEY = 'dh.theme.selected';

// Default theme when no selection exists
export const DEFAULT_THEME_ID = 'docuHounds';

// All valid theme IDs (dynamically derived from registry)
export const AVAILABLE_THEME_IDS = THEME_IDS;

/**
 * Validate if a theme ID is valid
 * @param {string} themeId - The theme ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function isValidTheme(themeId) {
  return typeof themeId === 'string' && AVAILABLE_THEME_IDS.includes(themeId);
}

/**
 * Get stored theme from localStorage
 * @returns {string|null} The stored theme ID or null if not found/error
 */
export function getStoredTheme() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store theme to localStorage with validation
 * @param {string} themeId - The theme ID to store
 * @returns {boolean} True if successful, false if invalid or error
 */
export function setStoredTheme(themeId) {
  if (!isValidTheme(themeId)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[theme] setStoredTheme: invalid theme ID', themeId);
    }
    return false;
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// Theme Migration
// ============================================================================

const DEBUG_THEME_MIGRATION = process.env.NODE_ENV !== 'production';

function dbg(...args) {
  if (DEBUG_THEME_MIGRATION) {
    // eslint-disable-next-line no-console
    console.debug('[theme:migrate]', ...args);
  }
}

/**
 * Unified migration pipeline for theme storage keys
 * Runs once during app initialization to migrate from legacy keys
 * @returns {{ themeId: string|null, migrated: boolean, source: string }}
 */
export function migrateThemeKeysOnce() {
  const OLD_KEY = 'ui.appTheme';
  
  // 1. Check if new key already exists and is valid
  const newTheme = getStoredTheme();
  if (newTheme && isValidTheme(newTheme)) {
    dbg('Using existing theme from new key:', newTheme);
    return { themeId: newTheme, migrated: false, source: 'new' };
  }
  
  // 2. Migrate from old key (ui.appTheme)
  try {
    const oldTheme = localStorage.getItem(OLD_KEY);
    if (oldTheme && isValidTheme(oldTheme)) {
      dbg('Migrating from old key:', OLD_KEY, '→', oldTheme);
      setStoredTheme(oldTheme);
      // Remove old key immediately after successful migration
      localStorage.removeItem(OLD_KEY);
      dbg('Migration complete, old key removed');
      return { themeId: oldTheme, migrated: true, source: 'ui.appTheme' };
    } else if (oldTheme) {
      dbg('Invalid theme in old key, removing:', oldTheme);
      localStorage.removeItem(OLD_KEY);
    }
  } catch (e) {
    dbg('Error reading old key:', e);
  }
  
  // 3. No legacy remapping: preserve explicit docuHounds selection if present
  // (Previously we remapped implicit docuHounds → primaryTheme; that behavior was removed.)
  
  // 4. No stored theme found
  dbg('No stored theme found, will use default');
  return { themeId: null, migrated: false, source: 'default' };
}
